import type { ReactNode } from 'react';
import { createContext, useContext, useEffect, useState } from 'react';
// Import the SDK's own KhalAuthContext so we can provide it in the tree.
// Without this, the SDK's internal useNats() → useKhalAuth() → useContext(KhalAuthContext)
// returns null because no KhalAuthContext.Provider exists — causing ALL org-scoped
// NATS subscriptions to bail out (empty orgId/userId).
import { KhalAuthContext as SdkKhalAuthContext } from '_sdk_app_real';
import { getEnterpriseConfig } from '../lib/tauri';
import { setSentryUser, setSentryInstance } from '../sentry';

/**
 * Derive the kernel's customer id (= orgId in the NATS subject ACL) from its
 * public hostname. `https://customer.example.com` → `customer`. The kernel's
 * ws-server sets `session.orgId = instanceId` where instanceId is the same
 * value, so matching it here is what lets subjects like
 * `khal.<customer>.desktop.<userId>.*` pass the access check.
 */
function orgIdFromServerUrl(serverUrl: string | undefined): string {
	if (!serverUrl) return 'desktop';
	try {
		const { hostname } = new URL(serverUrl);
		// Strip .khal.ai (or any apex) — take the left-most subdomain
		const first = hostname.split('.')[0];
		return first || 'desktop';
	} catch {
		return 'desktop';
	}
}

/** Authenticated user state — matches KhalAuth from @khal-os/sdk/app. */
export interface KhalAuth {
	userId: string;
	orgId: string;
	role: string;
	permissions: string[];
	/** Raw platform JWT. Installed apps use this to call their BFF with Authorization. */
	token?: string;
	loading: boolean;
	email?: string;
	name?: string;
	picture?: string;
}

const TauriAuthContext = createContext<KhalAuth | null>(null);

export function useTauriKhalAuth(): KhalAuth | null {
	return useContext(TauriAuthContext);
}

export function TauriKhalAuthProvider({ children }: { children: ReactNode }) {
	const [auth, setAuth] = useState<KhalAuth | null>({
		userId: '',
		orgId: '',
		role: '',
		permissions: [],
		loading: true,
	});

	useEffect(() => {
		getEnterpriseConfig()
			.then((config) => {
				if (!config) {
					// Not authenticated — fall back to local desktop identity
					setAuth({
						userId: 'desktop',
						orgId: 'desktop',
						role: 'platform-owner',
						permissions: [],
						loading: false,
					});
					return;
				}
				// Decode JWT payload (base64url) to extract user info
				try {
					const payload = JSON.parse(atob(config.token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/')));

					// Evict stale pre-G-USER tokens (no email claim) from localStorage.
					// These were written before 2026-04-17 and would render the UserMenu
					// as raw user_01XXX with no avatar. Catches entries that pre-date the
					// AuthAcceptScreen stale-token guard. PWA only — Tauri has its own
					// kernel config path and doesn't hit this branch for real sessions.
					const isTauri = typeof window !== 'undefined' && !!(window as { __TAURI__?: unknown }).__TAURI__;
					const hasEmailClaim = typeof payload.email === 'string' && payload.email.length > 0;
					if (!isTauri && !hasEmailClaim) {
						console.warn('[auth] stored JWT missing email claim — evicting stale token and redirecting to /auth/reset');
						window.location.replace('/auth/reset');
						return;
					}

					const userId = payload.sub || payload.userId || 'desktop';
					// Platform-signed JWT (G6) has no orgId claim — derive from kernel
					// hostname so NATS subject ACL (`khal.${orgId}.*`) matches the
					// server's `session.orgId = instanceId`. Legacy WorkOS JWTs that
					// carry `orgId` explicitly still win via the || short-circuit.
					const orgId = payload.orgId || orgIdFromServerUrl(config.serverUrl);
					setAuth({
						userId,
						orgId,
						role: payload.role || 'member',
						permissions: payload.permissions || [],
						token: config.token,
						loading: false,
						// Profile fields forwarded from platform JWT. Kept optional so
						// legacy WorkOS-signed tokens without these claims still work.
						...(typeof payload.email === 'string' && payload.email.length > 0
							? { email: payload.email }
							: {}),
						...(typeof payload.name === 'string' && payload.name.length > 0
							? { name: payload.name }
							: {}),
						...(typeof payload.picture === 'string' && payload.picture.length > 0
							? { picture: payload.picture }
							: {}),
					});

					// Enrich Sentry context with user and instance info
					setSentryUser(userId, orgId);
					if (config.serverUrl) {
						setSentryInstance(config.serverUrl, payload.env || 'production');
					}
				} catch {
					// If token isn't a valid JWT, use defaults
					setAuth({
						userId: 'desktop',
						orgId: 'desktop',
						role: 'member',
						permissions: [],
						loading: false,
					});
				}
			})
			.catch(() => {
				setAuth({
					userId: 'desktop',
					orgId: 'desktop',
					role: 'platform-owner',
					permissions: [],
					loading: false,
				});
			});
	}, []);

	// Provide BOTH contexts:
	// 1. SdkKhalAuthContext — for the SDK's internal useNats() → useKhalAuth() → useContext(KhalAuthContext)
	// 2. TauriAuthContext — for any direct useTauriKhalAuth() consumers in desktop code
	return (
		<SdkKhalAuthContext.Provider value={auth}>
			<TauriAuthContext.Provider value={auth}>{children}</TauriAuthContext.Provider>
		</SdkKhalAuthContext.Provider>
	);
}
